import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Search, X, Check, ChevronDown } from 'lucide-react';

/**
 * EntityPicker — reusable typeahead dropdown for any entity type supported by /api/search.
 *
 * Usage:
 *   <EntityPicker type="people" value={personId} onChange={setPersonId} placeholder="Assign to..." />
 *   <EntityPicker type="outlets" divisionId="me" ... />
 *   <EntityPicker type="vendors" ... />
 *
 * Props:
 *   type: 'people' | 'outlets' | 'vendors' | 'pipeline' | 'sops' | 'contracts'
 *   value: selected id (string) or null
 *   onChange: (id, item) => void
 *   returnField: 'id' (default) | 'title' — use 'title' for backward-compatible name-string fields
 *   valueLabel: fallback label to show if value is set but item not yet hydrated
 *   placeholder: input placeholder
 *   required: boolean — shows red asterisk
 *   disabled: boolean
 *   filterBy: optional client-side filter fn(item) => boolean
 *   className: wrapper class
 *   testId: data-testid root
 */
export default function EntityPicker({
  type,
  value = null,
  onChange,
  returnField = 'id',
  valueLabel = '',
  placeholder = 'Search…',
  required = false,
  disabled = false,
  filterBy,
  className = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState(valueLabel);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Fetch when query changes
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setItems([]); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}&types=${type}&limit=8`);
        let results = r.data.results?.[0]?.results || [];
        if (filterBy) results = results.filter(filterBy);
        setItems(results);
        setActiveIdx(0);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(h);
  }, [q, type, open, filterBy]);

  // Hydrate selected label if value set but we don't have it.
  // For returnField='title', the value itself IS the label so use it directly.
  useEffect(() => {
    if (!value) { setSelectedLabel(''); return; }
    if (returnField === 'title') { setSelectedLabel(value); return; }
    if (!selectedLabel) setSelectedLabel(valueLabel || '');
  }, [value, valueLabel, selectedLabel, returnField]);

  const pick = (item) => {
    const ret = returnField === 'title' ? item.title : item.id;
    onChange?.(ret, item);
    setSelectedLabel(item.title);
    setOpen(false);
    setQ('');
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(null, null);
    setSelectedLabel('');
    setQ('');
  };

  const onKey = (e) => {
    if (!open) { if (e.key === 'Enter' || e.key === 'ArrowDown') { setOpen(true); e.preventDefault(); } return; }
    if (e.key === 'ArrowDown') { setActiveIdx(i => Math.min(items.length - 1, i + 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setActiveIdx(i => Math.max(0, i - 1)); e.preventDefault(); }
    else if (e.key === 'Enter' && items[activeIdx]) { pick(items[activeIdx]); e.preventDefault(); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => { if (!disabled) { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 30); } }}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-xs text-left bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: value ? '#2A7B88' : '#E5E1DC' }}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {value && selectedLabel ? (
          <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{selectedLabel}</span>
        ) : (
          <span className="flex-1" style={{ color: '#9CA3AF' }}>{placeholder}{required ? ' *' : ''}</span>
        )}
        {value && !disabled ? (
          <button type="button" onClick={clear} className="p-0.5 hover:bg-red-50 rounded" title="Clear"><X size={11} style={{ color: '#D4726A' }} /></button>
        ) : (
          <ChevronDown size={12} style={{ color: '#9CA3AF' }} />
        )}
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-md border bg-white shadow-lg max-h-64 overflow-hidden flex flex-col" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b" style={{ borderColor: '#F3F4F6' }}>
            <Search size={11} style={{ color: '#9CA3AF' }} />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder={`Search ${type}…`}
              className="flex-1 text-xs outline-none"
              data-testid={testId ? `${testId}-input` : undefined}
            />
            {loading && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>…</span>}
          </div>
          <div className="overflow-y-auto flex-1">
            {q.trim().length < 2 && (
              <p className="text-[10px] px-3 py-3 text-center" style={{ color: '#9CA3AF' }}>Type 2+ characters…</p>
            )}
            {q.trim().length >= 2 && items.length === 0 && !loading && (
              <p className="text-[10px] px-3 py-3 text-center" style={{ color: '#9CA3AF' }}>No {type} found.</p>
            )}
            {items.map((it, idx) => (
              <button
                type="button"
                key={it.id}
                onClick={() => pick(it)}
                onMouseEnter={() => setActiveIdx(idx)}
                className="w-full text-left px-3 py-1.5 flex items-start gap-2 border-l-2"
                style={{
                  backgroundColor: idx === activeIdx ? '#F0F9FF' : 'transparent',
                  borderLeftColor: idx === activeIdx ? '#378ADD' : 'transparent',
                }}
                data-testid={testId ? `${testId}-option-${it.id}` : undefined}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{it.title}</p>
                  <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>{it.subtitle}</p>
                </div>
                {value === it.id && <Check size={12} style={{ color: '#2D8B5E' }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
